import React, { useMemo } from 'react'
import cx from 'classnames'
import { Link as ReactRouterLink, LinkProps as ReactRouterLinkProps } from 'react-router-dom'
import * as Icon from '@manychat/icons'

import cleanProps from '../../lib/clean-react-props'
import { fixedForwardRef } from '../../utils/fixedForwardRef'

import {
  IconNames,
  As,
  LinkProps,
  LinkAsAnchorProps,
  LinkAsButtonProps,
  LinkAsRouterLinkProps,
} from './interfaces'

import cm from './Link.module.css'

function isButtonProps(props: { as?: As }): props is LinkAsButtonProps<'button'> {
  return props.as === 'button'
}

function isRouterLinkProps(props: {
  as?: As
}): props is LinkAsRouterLinkProps<typeof ReactRouterLink> {
  return props.as === ReactRouterLink
}

export const LinkComponent = <T extends As = 'a'>(
  props: LinkProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) => {
  const {
    variant = 'body',
    contentColor = 'default',
    underline = false,
    title,
    id,
    className,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    ariaDetails,
    ariaHiddenText,
    tabIndex,
    leftIcon,
    rightIcon,
    children,
    ...rest
  } = props
  const restProps = rest

  const LeftIconComponent = leftIcon ? Icon[leftIcon as IconNames] : null
  const RightIconComponent = rightIcon ? Icon[rightIcon as IconNames] : null

  const linkClasses = cx(
    cm.link,
    {
      [cm.subheading]: variant === 'subheading',
      [cm.body]: variant === 'body',
      [cm.bodyAccented]: variant === 'body-accented',
      [cm.small]: variant === 'small',
      [cm.white]: contentColor === 'white',
      [cm.subtle]: contentColor === 'subtle',
      [cm.underline]: underline,
    },
    className,
  )

  const linkContent = useMemo(
    () => (
      <>
        {LeftIconComponent && (
          <LeftIconComponent aria-hidden="true" focusable="false" className={cm.icon} />
        )}
        <span className={cm.linkText} aria-hidden={ariaHiddenText}>
          {children}
        </span>
        {RightIconComponent && (
          <RightIconComponent aria-hidden="true" focusable="false" className={cm.icon} />
        )}
      </>
    ),
    [LeftIconComponent, RightIconComponent, ariaHiddenText, children],
  )
  if (isButtonProps(restProps)) {
    const { as: _, onClick, type, ...buttonRestProps } = restProps as LinkAsButtonProps<'button'>
    return (
      <button
        {...cleanProps(buttonRestProps, [], [])}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        onClick={onClick}
        type={type ?? 'button'}
        className={linkClasses}
        tabIndex={tabIndex}
        title={title}
        id={id}
        ref={ref as typeof buttonRestProps['ref']}
      >
        {linkContent}
      </button>
    )
  } else if (isRouterLinkProps(restProps)) {
    const { as: LinkComponent, ...routerLinkRestProps } = restProps as LinkAsRouterLinkProps<
      typeof ReactRouterLink
    >

    const { to } = props as ReactRouterLinkProps
    return (
      <LinkComponent
        {...routerLinkRestProps}
        to={to}
        className={linkClasses}
        title={title}
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        tabIndex={tabIndex}
        ref={ref as typeof routerLinkRestProps['ref']}
      >
        {linkContent}
      </LinkComponent>
    )
  } else {
    const { as: _, href, ...linkRestProps } = restProps as LinkAsAnchorProps<'a'>
    return (
      <a
        rel="noreferrer"
        {...cleanProps(linkRestProps, [], [])}
        href={href}
        className={linkClasses}
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        tabIndex={tabIndex}
        title={title}
        ref={ref as typeof linkRestProps['ref']}
      >
        {linkContent}
      </a>
    )
  }
}

export const Link = fixedForwardRef(LinkComponent)
